import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Bot, ImageIcon, ScanText, Headset, Crown, Settings, LogOut, ShieldHalf, Volume2, X, Menu, FileText, Lock } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { speak } from "@/lib/audio";
import { setOrbTheme } from "@/lib/orb";

import { shareLocation, enablePush, pushSupported } from "@/lib/pwa";
import { AuroraBackground } from "@/components/AuroraBackground";
import { Logo } from "@/components/Brand";
import { Footer } from "@/components/Footer";
import { Assistant } from "@/components/app/Assistant";
import { ImagesView } from "@/components/app/ImagesView";
import { OcrView } from "@/components/app/OcrView";
import { SupportView } from "@/components/app/SupportView";
import { BillingView } from "@/components/app/BillingView";
import { SettingsView } from "@/components/app/SettingsView";
import { PhoneGate } from "@/components/app/PhoneGate";

const TABS = [
  { id: "assistente", label: "Assistente", Icon: Bot },
  { id: "immagini", label: "Immagini", Icon: ImageIcon },
  { id: "ocr", label: "OCR", Icon: ScanText },
  { id: "supporto", label: "Supporto", Icon: Headset },
  { id: "abbonamento", label: "Abbonamento", Icon: Crown },
  { id: "impostazioni", label: "Impostazioni", Icon: Settings },
];

function useWelcome(user) {
  const [pending, setPending] = useState(null);
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current || !user) return;
    ran.current = true;
    if (sessionStorage.getItem("aurora_greeted") || user.settings?.welcome_voice === false) return;
    sessionStorage.setItem("aurora_greeted", "1");
    const first = (user.name || "").split(" ")[0];
    const text = `Ciao ${first}, sono Aurora. Come posso aiutarti oggi? Scrivi nel prompt.`;
    speak(text, user.settings?.tts_voice).catch(() => setPending(text));
  }, [user]);
  const play = () => { const t = pending; setPending(null); speak(t, user.settings?.tts_voice).catch(() => {}); };
  return { pending, play, dismiss: () => setPending(null) };
}

function useHeartbeat() {
  useEffect(() => {
    const t = setInterval(() => { if (document.visibilityState === "visible") api.post("/usage/heartbeat").catch(() => {}); }, 60000);
    return () => clearInterval(t);
  }, []);
}

const PermBanner = () => {
  const [show, setShow] = useState(() => !localStorage.getItem("aurora_perm_done"));
  if (!show) return null;
  const close = () => { localStorage.setItem("aurora_perm_done", "1"); setShow(false); };
  return (
    <div className="mx-4 sm:mx-8 mt-3 rounded-xl border border-cyan-500/25 bg-[#0a1224]/85 backdrop-blur-xl px-3 sm:px-4 py-2 flex items-center gap-2 text-xs text-slate-300" data-testid="permissions-banner">
      <span className="flex-1 min-w-0 truncate"><span className="sm:hidden">Attiva:</span><span className="hidden sm:inline">Attiva notifiche e posizione per un'esperienza completa.</span></span>
      <button className="btn-ghost-hud rounded-full px-3 py-1.5" data-testid="banner-location-btn" onClick={() => shareLocation().then(() => toast.success("Posizione salvata")).catch((e) => toast.error(e.message))}>Posizione</button>
      {pushSupported() && <button className="btn-ghost-hud rounded-full px-3 py-1.5" data-testid="banner-push-btn" onClick={() => enablePush().then(() => toast.success("Notifiche attivate")).catch((e) => toast.error(e.message))}>Notifiche</button>}
      <button onClick={close} className="text-slate-500 hover:text-white" data-testid="banner-close-btn"><X size={15} /></button>
    </div>
  );
};

const MobileMenu = ({ tabs, tab, setTab, unread, isAdmin, logout }) => {
  const nav = useNavigate();
  const cur = tabs.find((t) => t.id === tab) || tabs[0];
  const item = "flex items-center gap-2 text-sm py-2.5";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="mobile-menu-btn" className="md:hidden relative btn-ghost-hud rounded-full pl-3 pr-3.5 py-1.5 text-xs flex items-center gap-1.5">
          <Menu size={15} /> {cur.label}
          {unread > 0 && <span className="w-2 h-2 rounded-full bg-amber-400 absolute -top-0.5 -right-0.5" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-[#050b18]/95 backdrop-blur-xl border-cyan-500/25 text-slate-200">
        {tabs.map(({ id, label, Icon }) => (
          <DropdownMenuItem key={id} onClick={() => setTab(id)} data-testid={`mobile-tab-${id}`} className={`${item} ${tab === id ? "text-cyan-300" : ""}`}>
            <Icon size={15} /> {label}{id === "supporto" && unread > 0 && <span className="ml-auto w-2 h-2 rounded-full bg-amber-400" />}
          </DropdownMenuItem>
        ))}
        {isAdmin && <DropdownMenuItem onClick={() => nav("/admin")} data-testid="mobile-tab-admin" className={`${item} text-amber-200`}><ShieldHalf size={15} /> Pannello SU</DropdownMenuItem>}
        <DropdownMenuSeparator className="bg-cyan-500/15" />
        <DropdownMenuItem onClick={() => nav("/termini-e-condizioni")} data-testid="mobile-menu-terms" className={`${item} text-slate-400`}><FileText size={15} /> Termini e Condizioni</DropdownMenuItem>
        <DropdownMenuItem onClick={() => nav("/privacy-policy")} data-testid="mobile-menu-privacy" className={`${item} text-slate-400`}><Lock size={15} /> Privacy Policy</DropdownMenuItem>
        <DropdownMenuItem onClick={logout} data-testid="mobile-menu-logout" className={`${item} text-rose-300`}><LogOut size={15} /> Esci</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default function AppShell() {
  const { user, setUser, refreshUser, logout } = useAuth();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const tabs = TABS.filter((t) => t.id !== "abbonamento" || user.subscriptions_enabled);
  const tab = tabs.some((t) => t.id === params.get("tab")) ? params.get("tab") : "assistente";
  const setTab = (id) => setParams(id === "assistente" ? {} : { tab: id });
  const welcome = useWelcome(user);
  const [unread, setUnread] = useState(0);
  useHeartbeat();
  useEffect(() => { if (user.settings?.orb_theme) setOrbTheme(user.settings.orb_theme); }, [user.settings?.orb_theme]);
  useEffect(() => {
    const f = () => api.get("/support/unread").then((r) => setUnread(r.data.unread)).catch(() => {});
    f();
    const t = setInterval(f, 15000);
    return () => clearInterval(t);
  }, [tab]);

  const doLogout = async () => { await logout(); nav("/"); };
  const isAdmin = ["admin", "superadmin"].includes(user.role);
  const active = user.subscription_active;

  const views = {
    assistente: <Assistant user={user} refreshUser={refreshUser} goBilling={() => setTab("abbonamento")} />,
    immagini: <ImagesView active={active} />,
    ocr: <OcrView active={active} />,
    supporto: <SupportView />,
    abbonamento: <BillingView user={user} />,
    impostazioni: <SettingsView user={user} setUser={setUser} logout={doLogout} />,
  };

  return (
    <div className="h-[100dvh] flex flex-col text-slate-200 overflow-hidden">
      <AuroraBackground />
      <header data-testid="app-header" className="shrink-0 z-40 backdrop-blur-2xl bg-[#030712]/85 border-b border-cyan-500/15">
        <div className="px-4 sm:px-6 py-2.5 flex items-center gap-3">
          <Logo to="/app" small />
          <nav className="hidden md:flex items-center gap-1 ml-4 flex-1">
            {tabs.map(({ id, label, Icon }) => (
              <button key={id} onClick={() => setTab(id)} data-testid={`tool-tab-${id}`}
                className={`relative flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors duration-200 ${tab === id ? "text-white bg-cyan-500/15" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
                <Icon size={15} /> {label}
                {id === "supporto" && unread > 0 && <span className="w-2 h-2 rounded-full bg-amber-400 absolute top-1.5 right-1.5" data-testid="support-unread-dot" />}
              </button>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            {isAdmin && <Link to="/admin" data-testid="tool-tab-admin" className="hidden md:flex btn-ghost-hud rounded-full px-3 py-1.5 text-xs items-center gap-1.5 !border-amber-400/50 !text-amber-200"><ShieldHalf size={14} /> Pannello SU</Link>}
            <button onClick={doLogout} data-testid="btn-logout" title="Esci" className="hidden md:flex w-9 h-9 rounded-full items-center justify-center text-slate-400 hover:text-rose-300 hover:bg-white/5"><LogOut size={16} /></button>
            <MobileMenu tabs={tabs} tab={tab} setTab={setTab} unread={unread} isAdmin={isAdmin} logout={doLogout} />
          </div>
        </div>
      </header>
      {welcome.pending && (
        <div className="mx-4 sm:mx-8 mt-3 rounded-xl border border-amber-400/40 bg-amber-500/10 px-4 py-2.5 flex items-center gap-3 text-sm" data-testid="welcome-voice-banner">
          <Volume2 size={16} className="text-amber-300" />
          <span className="flex-1">Aurora vuole salutarti.</span>
          <button onClick={welcome.play} className="btn-aurora rounded-full px-4 py-1.5 text-xs" data-testid="welcome-play-btn">Ascolta</button>
          <button onClick={welcome.dismiss} className="text-slate-500"><X size={14} /></button>
        </div>
      )}
      {tab === "assistente" && <PermBanner />}
      <PhoneGate user={user} setUser={setUser} />
      <main className="flex-1 min-h-0 overflow-y-auto">
        {tab === "assistente" ? views.assistente : (
          <div className="min-h-full flex flex-col"><div className="flex-1">{views[tab]}</div><Footer /></div>
        )}
      </main>
    </div>
  );
}
